export type SalesPoint = { date: string; quantity: number; revenue: number }
export type Metric = 'quantity' | 'revenue'
export type CompareMode = 'previous' | 'year' | 'none'

/** Trailing N-day moving average of `metric`. Early points use the shorter available window. */
export function movingAverage(series: SalesPoint[], window: number, metric: Metric): { date: string; value: number }[] {
  return series.map((pt, i) => {
    const slice = series.slice(Math.max(0, i - window + 1), i + 1)
    const avg = slice.reduce((s, p) => s + p[metric], 0) / slice.length
    return { date: pt.date, value: avg }
  })
}

/** Sum `metric` into consecutive 7-day buckets (label = first day of each bucket). */
export function bucketByWeek(series: SalesPoint[], metric: Metric): { date: string; value: number }[] {
  const out: { date: string; value: number }[] = []
  for (let i = 0; i < series.length; i += 7) {
    const slice = series.slice(i, i + 7)
    out.push({ date: slice[0].date, value: slice.reduce((s, p) => s + p[metric], 0) })
  }
  return out
}

/** Sum of `metric` for current vs comparison + pct change (null when no comparison data). */
export function periodDelta(
  current: SalesPoint[],
  comparison: SalesPoint[],
  metric: Metric,
): { sum: number; prevSum: number; pct: number | null } {
  const sum = current.reduce((s, p) => s + p[metric], 0)
  const prevSum = comparison.reduce((s, p) => s + p[metric], 0)
  const pct = prevSum > 0 ? ((sum - prevSum) / prevSum) * 100 : null
  return { sum, prevSum, pct }
}

const DAY_MS = 86_400_000

function isoUTC(d: Date): string {
  return d.toISOString().slice(0, 10)
}

/**
 * Comparison window for an inclusive [from,to] range.
 * previous = equal-length block immediately before; year = same dates −1 year.
 * Dates are treated in UTC to avoid timezone drift.
 */
export function comparisonWindow(from: string, to: string, mode: CompareMode): { from: string; to: string } | null {
  if (mode === 'none') return null
  const f = new Date(`${from}T00:00:00Z`)
  const t = new Date(`${to}T00:00:00Z`)
  if (mode === 'year') {
    const fy = new Date(f); fy.setUTCFullYear(fy.getUTCFullYear() - 1)
    const ty = new Date(t); ty.setUTCFullYear(ty.getUTCFullYear() - 1)
    return { from: isoUTC(fy), to: isoUTC(ty) }
  }
  const lengthDays = Math.round((t.getTime() - f.getTime()) / DAY_MS) + 1  // inclusive
  const prevTo = new Date(f.getTime() - DAY_MS)
  const prevFrom = new Date(prevTo.getTime() - (lengthDays - 1) * DAY_MS)
  return { from: isoUTC(prevFrom), to: isoUTC(prevTo) }
}

/** Continuous daily series over [from,to]; missing days filled with 0 (0 is a valid day value). */
export function densifyDaily(series: SalesPoint[], from: string, to: string): SalesPoint[] {
  const byDate = new Map(series.map((p) => [p.date, p]))
  const out: SalesPoint[] = []
  const end = new Date(`${to}T00:00:00Z`).getTime()
  for (let t = new Date(`${from}T00:00:00Z`).getTime(); t <= end; t += DAY_MS) {
    const date = new Date(t).toISOString().slice(0, 10)
    out.push(byDate.get(date) ?? { date, quantity: 0, revenue: 0 })
  }
  return out
}

/** Fold comparison values onto current bars by RELATIVE day index (not absolute date). */
export function withComparison(
  bars: { date: string; value: number }[],
  comparison: { date: string; value: number }[],
): { date: string; value: number; cmpValue: number | null }[] {
  return bars.map((b, i) => ({ ...b, cmpValue: comparison[i]?.value ?? null }))
}
